import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWhatsAppService from '../../services/adminWhatsAppService';
import { CAMPAIGN_STATUS_COUNT_ORDER, formatShortDate, sortCampaignsForDisplay } from '../../utils/whatsapp';
import LoadingScreen from '../LoadingScreen';
import CampaignDetail from './CampaignDetail';
import CampaignStatusBadge from './CampaignStatusBadge';

/**
 * Campaigns tab: every campaign with its outcome counts; opening one shows its recipients.
 * `initialCampaignId` opens that campaign at once (the wizard uses it after a send).
 * @param {{initialCampaignId?: string}} props
 */
const CampaignsTab = ({ initialCampaignId }) => {
  const toast = useToast();
  const [campaigns, setCampaigns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(initialCampaignId ?? null);
  const latest = useRef(0);

  useEffect(
    () => () => {
      latest.current += 1;
    },
    [],
  );

  const load = useCallback(async () => {
    const ticket = ++latest.current;
    setLoading(true);
    try {
      const data = await adminWhatsAppService.listCampaigns();
      if (ticket !== latest.current) return;
      setCampaigns(sortCampaignsForDisplay(data));
      setError('');
    } catch (err) {
      if (ticket !== latest.current) return;
      setError(err.message);
      toast.error(err.message);
    } finally {
      if (ticket === latest.current) setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (selectedId) {
    return (
      <CampaignDetail
        campaignId={selectedId}
        onBack={() => {
          setSelectedId(null);
          load();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-medium text-foreground">Campaigns</h2>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          aria-label="Refresh campaigns"
          className="p-2 border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {campaigns === null && loading && <LoadingScreen />}

      {campaigns === null && !loading && error && (
        <div className="p-6 text-center bg-card border border-border rounded-lg">
          <p className="font-medium text-foreground">Could not load campaigns</p>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <button
            type="button"
            onClick={load}
            className="mt-4 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Try again
          </button>
        </div>
      )}

      {campaigns !== null && campaigns.length === 0 && (
        <div className="p-6 text-center bg-card border border-border rounded-lg">
          <p className="font-medium text-foreground">No campaigns yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Create one from the New campaign tab and it will appear here.
          </p>
        </div>
      )}

      {campaigns !== null && campaigns.length > 0 && (
        <ul className="space-y-3">
          {campaigns.map((campaign) => (
            <li key={campaign.id}>
              <button
                type="button"
                data-testid={`campaign-${campaign.id}`}
                onClick={() => setSelectedId(campaign.id)}
                className="w-full text-left p-4 bg-card border border-border rounded-lg hover:border-muted-foreground transition-colors"
              >
                <span className="flex flex-wrap items-start justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block font-medium text-foreground break-words">{campaign.name}</span>
                    <span className="block text-sm text-muted-foreground break-words">
                      {campaign.template_name || 'Template removed'} - {formatShortDate(campaign.created_at)}
                    </span>
                  </span>
                  <CampaignStatusBadge
                    status={campaign.status}
                    scheduledAt={campaign.scheduled_at}
                    pausedReason={campaign.paused_reason}
                  />
                </span>
                <span className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                  {CAMPAIGN_STATUS_COUNT_ORDER.map(({ status, label }) => (
                    <span key={status} className="block p-2 bg-muted/50 rounded-lg">
                      <span className="block text-xs text-muted-foreground">{label}</span>
                      <span
                        data-testid={`count-${campaign.id}-${status}`}
                        className="block text-base font-medium text-foreground"
                      >
                        {campaign.counts?.[status] || 0}
                      </span>
                    </span>
                  ))}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CampaignsTab;
